import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { format } from 'date-fns';

const MonthlyReport = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchMonthlyData();
  }, [selectedYear, selectedMonth]);

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const statsResponse = await axios.get(
        `${API}/stats/monthly?year=${selectedYear}&month=${selectedMonth}`
      );
      setStats(statsResponse.data);

      const ridesResponse = await axios.get(
        `${API}/rides?start_date=${statsResponse.data.month_start}&end_date=${statsResponse.data.month_end}`
      );
      setRides(ridesResponse.data);
    } catch (error) {
      toast.error('Failed to load monthly data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/export/csv?start_date=${stats.month_start}&end_date=${stats.month_end}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_report_${selectedYear}_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading report...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monthly-report-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Monthly Report</h2>
          <p className="text-gray-600 mt-1">{months[selectedMonth - 1]} {selectedYear}</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
            <SelectTrigger className="w-32" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month, idx) => (
                <SelectItem key={idx} value={(idx + 1).toString()}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
            <SelectTrigger className="w-24" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Rides</div>
            <div className="text-2xl font-bold">{stats?.total_rides || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Miles</div>
            <div className="text-2xl font-bold">{stats?.total_miles.toFixed(1) || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Gross Earnings</div>
            <div className="text-2xl font-bold text-blue-600">${stats?.gross_earnings.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Tips</div>
            <div className="text-2xl font-bold text-green-600">${stats?.total_tips.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Base Earnings</span>
            <span className="font-semibold">${stats?.total_earnings.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Tips</span>
            <span className="font-semibold">${stats?.total_tips.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-gray-700">Gross Total</span>
            <span className="font-semibold text-lg">${stats?.gross_earnings.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pb-3 border-b">
            <span className="text-orange-600">Commission (20%)</span>
            <span className="font-semibold text-orange-600">-${stats?.total_commission.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between items-center pt-2">
            <span className="text-lg font-bold">Net Earnings</span>
            <span className="text-2xl font-bold text-green-600">${stats?.net_earnings.toFixed(2) || '0.00'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Rides Table */}
      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle>Rides This Month ({rides.length})</CardTitle>
          <Button
            onClick={handleExport}
            disabled={rides.length === 0}
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          {rides.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No rides for this month</p>
          ) : (
            <div className="space-y-3">
              {rides.map((ride) => (
                <div
                  key={ride.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`monthly-ride-${ride.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{ride.client_name}</div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(ride.date), 'EEE, MMM dd, yyyy')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">${ride.net_earnings.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">net</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {ride.pickup_location} → {ride.dropoff_location}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>{ride.miles_driven} mi</span>
                    <span>•</span>
                    <span>${ride.total_amount.toFixed(2)} total</span>
                    <span>•</span>
                    <span>${ride.commission_amount.toFixed(2)} commission</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthlyReport;
